import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Search, ArrowRight, CornerDownLeft } from "lucide-react";
import { EASE } from "../ui/Reveal";
import { SOLUTION_KEYS, solutionPath } from "../../lib/solutions";


// Searches every solution category and offering; picks a result with mouse or keyboard.
const SiteSearch = ({ autoFocus = false, inline = false, onNavigate, className = "" }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

  const index = useMemo(() => {
    const items = [];
    SOLUTION_KEYS.forEach((key) => {
      const category = t(`whatWeDo.categories.${key}.title`);
      const to = solutionPath(key);
      items.push({ to, title: category, group: t("nav.services") });
      t(`whatWeDo.categories.${key}.offerings`, { returnObjects: true }).forEach((o) =>
        items.push({ to, title: o.title, meta: category, group: t("nav.services") })
      );
    });
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, i18n.language]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return index.filter((i) => !i.meta).slice(0, 7);
    return index
      .filter((i) => i.title.toLowerCase().includes(q) || (i.meta || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, index]);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    if (!autoFocus) return undefined;
    const id = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(id);
  }, [autoFocus]);

  // Ctrl/Cmd + K focuses the search from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const go = (item) => {
    navigate(item ? item.to : "/contact");
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
    onNavigate?.();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <label
        className={`flex items-center gap-2.5 h-11 rounded-xl border px-3.5 transition-colors duration-300 ${
          open ? "border-white/40 bg-white/10" : "border-white/15 bg-white/[0.06] hover:border-white/30"
        }`}
      >
        <Search size={16} className="text-white/50 flex-shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={t("nav.search")}
          aria-label={t("nav.search")}
          role="combobox"
          aria-expanded={open}
          aria-controls="site-search-results"
          className="flex-1 min-w-0 bg-transparent text-sm text-white placeholder:text-white/40 outline-none"
        />
        <kbd className="hidden sm:inline-block font-mono text-[10px] text-white/45 border border-white/20 rounded px-1.5 py-0.5" dir="ltr">
          {isMac ? "⌘K" : "Ctrl K"}
        </kbd>
      </label>

        {(open || inline) && (
          <motion.ul
            id="site-search-results"
            role="listbox"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: EASE }}
            className={
              inline
                ? "mt-3 rounded-2xl"
                : "absolute inset-x-0 top-[calc(100%+8px)] z-50 rounded-2xl border border-white/10 bg-[#141414] p-2 shadow-2xl"
            }
          >
            {results.length ? (
              results.map((item, i) => (
                <li key={`${item.to}-${item.title}`} role="option" aria-selected={i === active}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(item)}
                    className={`w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-start transition-colors ${
                      i === active ? "bg-white/10" : ""
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-white">{item.title}</span>
                      <span className="block truncate text-[11px] text-white/45">{item.meta || item.group}</span>
                    </span>
                    {i === active ? (
                      <CornerDownLeft size={14} className="text-rose-light flex-shrink-0" />
                    ) : (
                      <ArrowRight size={14} className={`text-white/30 flex-shrink-0 rtl:rotate-180`} />
                    )}
                  </button>
                </li>
              ))
            ) : (
              <li>
                <button
                  type="button"
                  onClick={() => go(null)}
                  className="w-full rounded-xl px-3 py-3 text-start text-sm text-white/60 hover:bg-white/10"
                >
                  {t("nav.noResults")}
                </button>
              </li>
            )}
          </motion.ul>
        )}
    </div>
  );
};

export default SiteSearch;
